/**
 * Video progress storage API.
 * Provides functions for persisting and retrieving video playback positions in localStorage.
 * Uses a cache key pattern with optional group key prefix for namespacing.
 */

/** localStorage key used to store all video progress data */
const VIDEO_PROGRESS_STORAGE_KEY = 'video-player-progress';

/** Minimum seconds watched before progress is saved (prevents saving for accidental clicks) */
const MIN_PROGRESS_THRESHOLD = 5;

/** Map of cache keys to their saved playback positions in seconds */
type VideoProgressMap = Record<string, number>;

/**
 * Type guard to validate that a parsed JSON value is a valid VideoProgressMap.
 */
const isVideoProgressMap = (value: unknown): value is VideoProgressMap => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return Object.entries(value).every(([key, val]) => typeof key === 'string' && typeof val === 'number');
};

/**
 * Retrieves all stored video progress data from localStorage.
 */
const getStoredProgress = (): VideoProgressMap => {
  try {
    const stored = localStorage.getItem(VIDEO_PROGRESS_STORAGE_KEY);
    if (!stored) {
      return {};
    }
    const parsed: unknown = JSON.parse(stored);
    return isVideoProgressMap(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Persists the video progress map to localStorage.
 */
const setStoredProgress = (progressMap: VideoProgressMap): void => {
  try {
    localStorage.setItem(VIDEO_PROGRESS_STORAGE_KEY, JSON.stringify(progressMap));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
};

/**
 * Saves the current playback position for a video.
 * Only saves if:
 * - The current time exceeds the minimum threshold (5 seconds)
 * - The current time is greater than any previously saved progress
 *
 * @param cacheKey - Unique key for the video (e.g., "groupKey:videoIdentifier")
 * @param currentTime - Current playback position in seconds
 */
export const saveVideoProgress = (cacheKey: string, currentTime: number): void => {
  if (currentTime < MIN_PROGRESS_THRESHOLD) {
    return;
  }

  const progressMap = getStoredProgress();
  const existingProgress = progressMap[cacheKey] ?? 0;

  if (currentTime > existingProgress) {
    setStoredProgress({ ...progressMap, [cacheKey]: currentTime });
  }
};

/**
 * Retrieves the saved playback position for a video.
 *
 * @param cacheKey - Unique key for the video
 * @returns The saved playback position in seconds, or 0 if not found
 */
export const getVideoProgress = (cacheKey: string): number => {
  const progressMap = getStoredProgress();
  return progressMap[cacheKey] ?? 0;
};

/**
 * Removes the saved progress for a specific video.
 *
 * @param cacheKey - Unique key for the video to clear
 */
export const clearVideoProgress = (cacheKey: string): void => {
  const progressMap = getStoredProgress();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [cacheKey]: _, ...rest } = progressMap;
  setStoredProgress(rest);
};

/**
 * Removes all saved video progress entries that belong to a specific group.
 * Entries are matched by their key prefix (cacheGroupKey + ":").
 *
 * @param cacheGroupKey - Group key prefix. Only entries starting with this prefix are removed.
 */
export const clearVideoProgressByGroup = (cacheGroupKey: string): void => {
  try {
    const progressMap = getStoredProgress();
    const prefix = `${cacheGroupKey}:`;
    const filteredProgress: VideoProgressMap = {};

    for (const [key, value] of Object.entries(progressMap)) {
      if (!key.startsWith(prefix)) {
        filteredProgress[key] = value;
      }
    }

    setStoredProgress(filteredProgress);
  } catch {
    // Ignore storage errors
  }
};
